// src/profile/profile.client.ts — „Profilul meu" (#/profil).
// Accesibil ORICĂRUI user autentificat (inclusiv cetățeanului) — NU e back-office, deci fără
// `guard_staff`. Emailul rămâne read-only: e identificatorul de login, iar `add_member` caută
// membrii după el. Schimbarea parolei cere parola curentă și e ascunsă pe conturile doar-SSO.

import "../styles.css"
import van from "vanjs-core"
import {
	session,
	session_loaded,
	change_password,
	update_profile,
	user_initials,
	user_label,
} from "../lib/session.client"
import { password_field } from "../lib/password.client"

const t = van.tags

const card =
	"bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-5 mb-6"
const h2 = "text-lg font-bold text-slate-900 dark:text-slate-100 mb-1"
const hint = "text-sm text-slate-500 dark:text-slate-400 mb-4"
const label = "block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
const input =
	"w-full rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
const btn =
	"px-4 py-2 rounded-lg bg-cyan-700 text-white text-sm font-semibold hover:bg-cyan-800 cursor-pointer disabled:opacity-60"

/** Linie de mesaj efemeră (verde la succes, roșu la eroare). */
function msg_line() {
	const text = van.state("")
	const bad = van.state(false)
	const el = t.span({
		class: () =>
			!text.val
				? "hidden"
				: bad.val
					? "ml-3 text-xs font-medium text-red-600 dark:text-red-400"
					: "ml-3 text-xs font-medium text-emerald-600 dark:text-emerald-400",
	})
	van.derive(() => (el.textContent = text.val))
	const say = (s: string, is_bad = false) => {
		text.val = s
		bad.val = is_bad
		if (s && !is_bad) {
			setTimeout(() => (text.val = ""), 2500)
		}
	}
	return { el, say }
}

/** Badge-uri cu rolurile: rolul de platformă + primăriile în care e membru. */
function RolesSection() {
	const badge = (cls: string, text: string) =>
		t.span({ class: `inline-block px-2 py-0.5 rounded-md text-xs font-semibold ${cls}` }, text)

	return t.div({ class: card }, () => {
		const s = session.val
		const pr = s.user?.platform_role
		const items: HTMLElement[] = []
		if (pr) {
			items.push(
				badge(
					"bg-amber-50 dark:bg-amber-950 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800",
					pr === "super" ? "Administrator platformă" : "Suport platformă",
				),
			)
		}
		for (const m of s.memberships) {
			items.push(
				badge(
					"bg-cyan-50 dark:bg-cyan-950 text-cyan-800 dark:text-cyan-300 border border-cyan-200",
					`${m.org_name} · ${m.role}`,
				),
			)
		}
		if (items.length === 0) {
			items.push(
				badge(
					"bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700",
					"Cetățean",
				),
			)
		}
		return t.div(
			t.h2({ class: h2 }, "Rolurile mele"),
			t.p({ class: hint }, "Se stabilesc de administrator; nu pot fi modificate de aici."),
			t.div({ class: "flex flex-wrap gap-2" }, ...items),
		)
	})
}

/** Numele afișat (editabil) + emailul (read-only). */
function IdentitySection() {
	const { el: msg, say } = msg_line()
	const busy = van.state(false)
	const name_input = t.input({ class: input, maxlength: "120", placeholder: "Nume și prenume" })

	// Pre-completează după hidratarea sesiunii (pagina se poate monta înainte de /api/auth/me).
	van.derive(() => {
		const u = session.val.user
		if (u) {
			name_input.value = u.display_name
		}
	})

	const save = async () => {
		if (busy.val) {
			return
		}
		busy.val = true
		const r = await update_profile(name_input.value)
		busy.val = false
		say(r.ok ? "Salvat ✓" : (r.message ?? "Eroare"), !r.ok)
	}

	return t.div(
		{ class: card },
		t.h2({ class: h2 }, "Datele contului", msg),
		t.p({ class: hint }, "Numele apare în aplicație și pe răspunsurile trimise cetățenilor."),
		t.div(
			{ class: "grid gap-4 sm:grid-cols-2" },
			t.div(t.label({ class: label }, "Nume complet"), name_input),
			t.div(
				t.label({ class: label }, "Email"),
				t.input({
					class: input + " bg-slate-50 dark:bg-slate-950 text-slate-500 dark:text-slate-400",
					readonly: true,
					value: () => session.val.user?.email ?? "",
				}),
				t.p(
					{ class: "text-xs text-slate-400 dark:text-slate-500 mt-1" },
					"Emailul e identificatorul de autentificare și nu poate fi schimbat.",
				),
			),
		),
		t.div(
			{ class: "mt-4" },
			t.button({ class: btn, disabled: () => busy.val, onclick: save }, () =>
				busy.val ? "Se salvează…" : "Salvează",
			),
		),
	)
}

/** Schimbarea parolei — ascunsă pentru conturile doar-SSO (`has_password === false`). */
function PasswordSection() {
	const { el: msg, say } = msg_line()
	const busy = van.state(false)
	// password_field (lib partajat): input-group cu „arată parola" — profilul lucrează pe .value
	const current_f = password_field({ autocomplete: "current-password" })
	const next_f = password_field({ autocomplete: "new-password" })
	const confirm_f = password_field({ autocomplete: "new-password" })
	const current = current_f.input
	const next = next_f.input
	const confirm = confirm_f.input

	const submit = async () => {
		if (busy.val) {
			return
		}
		if (next.value !== confirm.value) {
			say("Parolele noi nu coincid.", true)
			return
		}
		busy.val = true
		const r = await change_password(current.value, next.value)
		busy.val = false
		if (r.ok) {
			current.value = ""
			next.value = ""
			confirm.value = ""
		}
		say(r.ok ? "Parolă schimbată ✓" : (r.message ?? "Eroare"), !r.ok)
	}

	// Nu returnăm `null` dintr-o funcție reactivă — ascundem prin `style` (regula din CLAUDE.md).
	return t.div(
		{ class: () => (session.val.user?.has_password ? card : "hidden") },
		t.h2({ class: h2 }, "Schimbă parola", msg),
		t.p(
			{ class: hint },
			"Îți cerem parola curentă, ca nimeni altcineva să nu-ți poată prelua contul.",
		),
		t.div(
			{ class: "grid gap-4 sm:grid-cols-3" },
			t.div(t.label({ class: label }, "Parola curentă"), current_f.el),
			t.div(t.label({ class: label }, "Parola nouă"), next_f.el),
			t.div(t.label({ class: label }, "Confirmă parola nouă"), confirm_f.el),
		),
		t.div(
			{ class: "mt-4" },
			t.button({ class: btn, disabled: () => busy.val, onclick: submit }, () =>
				busy.val ? "Se schimbă…" : "Schimbă parola",
			),
		),
	)
}

export function ProfilePage(): HTMLElement {
	return t.div({ class: "min-h-[calc(100vh-3rem)] bg-slate-50 dark:bg-slate-950" }, () => {
		if (!session_loaded.val) {
			return t.div(
				{
					class:
						"flex justify-center items-center py-24 text-slate-400 dark:text-slate-500 animate-pulse",
				},
				"Se încarcă…",
			)
		}
		if (!session.val.authenticated) {
			return t.div(
				{ class: "max-w-lg mx-auto px-4 py-20 text-center" },
				t.div({ class: "text-4xl" }, "🔒"),
				t.h1(
					{ class: "mt-4 text-xl font-bold text-slate-800 dark:text-slate-200" },
					"Autentificare necesară",
				),
				t.a(
					{
						href: `#/autentificare?return=${encodeURIComponent("/profil")}`,
						class: "inline-block mt-6 " + btn,
					},
					"Autentificare",
				),
			)
		}
		const u = session.val.user
		return t.div(
			{ class: "max-w-3xl mx-auto px-4 py-8" },
			t.div(
				{ class: "flex items-center gap-3 mb-6" },
				t.div(
					{
						class:
							"w-12 h-12 rounded-full bg-cyan-700 text-white flex items-center justify-center " +
							"font-bold text-sm flex-shrink-0",
					},
					user_initials(u),
				),
				t.div(
					t.h1(
						{ class: "text-2xl font-extrabold text-slate-900 dark:text-slate-100 leading-tight" },
						user_label(u),
					),
					t.div({ class: "text-sm text-slate-500 dark:text-slate-400" }, u?.email ?? ""),
				),
			),
			IdentitySection(),
			PasswordSection(),
			RolesSection(),
		)
	})
}
